/**
 * Bringing a legacy account over — the SEED lifecycle, kept apart from the
 * connection status.
 *
 * The first time a UserDo is opened it seeds itself from the user's legacy
 * export (`party/server/user/legacy-migration.ts`). That can take a moment
 * (`seeding`), or be refused and re-asked on a backoff for as long as it
 * takes (`waiting`) — while the export has not landed yet, while R2 is
 * unreachable, and even while a broken export object waits for us to fix it.
 * There is no "failed" outcome: every legacy account seeds, and the client
 * never stops asking. None of that says anything about the socket: it is open the
 * whole time. Folding it into `SyncStatus` (it used to read `migrating`, then
 * `error`) made three consumers misread it — the onboarding gate took a
 * waiting user's empty store as "settled, new user", the connection banner
 * said "Connection lost", and cold-deck hydration treated it as offline.
 *
 * So this is its own state, derived from the `sync-state` messages the
 * adapter already receives — nothing new on the wire:
 *
 *   unknown     no `sync-state` yet this session (or since a user switch)
 *   not-needed  the first answer was `ready`: an account seeded long ago
 *   seeding     `needs-seed` / `migrating`: a run is reading the export
 *   waiting     any `migration-error` (but the lock's `in-progress`): the
 *               client re-asks by itself, on a longer backoff when the DO
 *               says retrying cannot help yet (`retryable: false`)
 *   done        `ready` after either of the two above
 *
 * Pure, so every transition is pinned (`account-seed.spec.ts`).
 */

export type AccountSeedPhase = 'unknown' | 'not-needed' | 'seeding' | 'waiting' | 'done';

export type AccountSeedState = {
  phase: AccountSeedPhase;
  /** The DO's `migration-error` reason (`export-not-ready`, …) while
   *  `waiting`; for logs and analytics, never shown raw. */
  reason?: string;
  /** Mirrors the last `migration-error.retryable`. */
  retryable?: boolean;
};

export const INITIAL_ACCOUNT_SEED_STATE: AccountSeedState = { phase: 'unknown' };

/** The `sync-state` messages (and the one client-side reset) that move it. */
export type AccountSeedEvent =
  | { type: 'needs-seed' }
  | { type: 'migrating' }
  | { type: 'migration-error'; reason: string; retryable: boolean }
  | { type: 'ready' }
  /** Signed-in identity changed (sign-out, account switch). */
  | { type: 'reset' };

/**
 * `in-progress` is the DO's lock answer: ANOTHER connection's run holds it,
 * and that run broadcasts `migrating`/`ready` to every tab. Nothing is wrong
 * and nothing is waiting on an export — it is still seeding.
 */
const STILL_SEEDING_REASONS = new Set(['in-progress']);

/** Same object when already seeding, so a progress tick re-renders nothing. */
const seeding = (prev: AccountSeedState): AccountSeedState =>
  prev.phase === 'seeding' ? prev : { phase: 'seeding' };

export function nextAccountSeedState(
  prev: AccountSeedState,
  event: AccountSeedEvent,
): AccountSeedState {
  switch (event.type) {
    case 'reset':
      return INITIAL_ACCOUNT_SEED_STATE;
    case 'needs-seed':
      // A reconnect while waiting re-asks on its own; until a run really
      // starts (`migrating`) the user is still where they were — flipping
      // the copy to "seeding" on every reconnect would flicker.
      if (prev.phase === 'waiting') return prev;
      return seeding(prev);
    case 'migrating':
      return seeding(prev);
    case 'migration-error': {
      if (STILL_SEEDING_REASONS.has(event.reason)) return seeding(prev);
      const phase = 'waiting';
      if (
        prev.phase === phase &&
        prev.reason === event.reason &&
        prev.retryable === event.retryable
      ) {
        return prev;
      }
      return { phase, reason: event.reason, retryable: event.retryable };
    }
    case 'ready':
      if (prev.phase === 'done' || prev.phase === 'not-needed') return prev;
      return { phase: prev.phase === 'unknown' ? 'not-needed' : 'done' };
  }
}

/** The DO is (or was, this session) bringing the account over and has not
 *  finished: the store may be empty for a reason that is not "new user". */
export function isAccountSeedPending(seed: AccountSeedState): boolean {
  return seed.phase === 'seeding' || seed.phase === 'waiting';
}

/** The DO answered and the store can be trusted to hold the account. */
export function isAccountSeedSettled(seed: AccountSeedState): boolean {
  return seed.phase === 'done' || seed.phase === 'not-needed';
}

/**
 * The "bringing your account over" surface: which copy. Keys live in the
 * shared bundle (`<lang>-account-seed.json`); both clients translate them and
 * inject their own shell.
 */
type SurfacePhase = 'seeding' | 'waiting';
export type AccountSeedCopyKey =
  | `accountSeed.${SurfacePhase}.title`
  | `accountSeed.${SurfacePhase}.description`;

export type AccountSeedSurface = {
  phase: SurfacePhase;
  titleKey: AccountSeedCopyKey;
  descriptionKey: AccountSeedCopyKey;
};

export function accountSeedSurface(seed: AccountSeedState): AccountSeedSurface | null {
  switch (seed.phase) {
    case 'seeding':
      return {
        phase: 'seeding',
        titleKey: 'accountSeed.seeding.title',
        descriptionKey: 'accountSeed.seeding.description',
      };
    case 'waiting':
      return {
        phase: 'waiting',
        titleKey: 'accountSeed.waiting.title',
        descriptionKey: 'accountSeed.waiting.description',
      };
    default:
      return null;
  }
}

/**
 * Should the full-screen seed surface cover the app? `seeding` always (a
 * run is writing the account right now — what is local is about to be
 * replaced); `waiting` only while the store holds nothing the user could
 * use, so a device with local data keeps working on it.
 */
export function shouldBlockOnAccountSeed(
  seed: AccountSeedState,
  storeHasData: boolean,
): boolean {
  if (seed.phase === 'seeding') return true;
  if (seed.phase === 'waiting') return !storeHasData;
  return false;
}
